const PIPELINE_LABELS = {
  video_to_llm: 'Vidéo → IA',
  video_to_llm_simple: 'Vidéo → IA Bench',
  images_to_llm: 'LLM+Images',
  images_to_ia: 'Images → IA',
  yolo_segmentation: 'YOLO+LLM',
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const defaultSteps = (pipeline) => {
  switch (pipeline) {
    case 'video_to_llm':
      return [
        { id: 'stage1_llm', label: 'Analyse LLM (vidéo)', status: 'completed' },
        { id: 'image_enrichment', label: 'Extraction & marquage des images', status: 'completed' },
      ];
    case 'images_to_llm':
    case 'images_to_ia':
      return [
        { id: 'frame_extraction', label: 'Extraction des frames', status: 'completed' },
        { id: 'llm_analysis', label: 'Analyse LLM', status: 'completed' },
        { id: 'image_marking', label: 'Marquage des dommages', status: 'completed' },
      ];
    case 'video_to_llm_simple':
      return [
        { id: 'simple_prompt', label: 'Analyse (prompt simple)', status: 'completed' },
      ];
    case 'yolo_segmentation':
      return [
        { id: 'frame_extraction', label: 'Extraction des images', status: 'completed' },
        { id: 'yolo_detection', label: 'Détection YOLO', status: 'completed' },
        { id: 'llm_synthesis', label: 'Synthèse LLM', status: 'completed' },
      ];
    default:
      return [{ id: 'analysis', label: 'Analyse', status: 'completed' }];
  }
};

const inferPipeline = (inspection, workflow) => {
  if (workflow.pipeline) {
    return workflow.pipeline;
  }

  if (workflow.inspection_method === 'yolo_segmentation') {
    return 'yolo_segmentation';
  }
  if (workflow.inspection_method === 'llm_from_images') {
    return 'images_to_ia';
  }

  const report = workflow.report || inspection?.report_v02 || {};
  if (report.pipeline) {
    return report.pipeline;
  }

  const version = report.version || workflow.version || inspection?.version || '';
  if (version.startsWith('yolo')) {
    return 'yolo_segmentation';
  }
  if (version.startsWith('images_to_ia') || version.startsWith('llm_images')) {
    return 'images_to_ia';
  }
  if (version.startsWith('llm_simple_prompt')) {
    return 'video_to_llm_simple';
  }
  return 'video_to_llm';
};

const extractDamages = (workflow, report) => {
  if (Array.isArray(workflow.damages) && workflow.damages.length > 0) {
    return workflow.damages;
  }
  const synthesisDamages = workflow.llm_synthesis?.damages;
  if (Array.isArray(synthesisDamages) && synthesisDamages.length > 0) {
    return synthesisDamages;
  }
  const collected = [];
  const reports = report?.reports;
  if (Array.isArray(reports)) {
    reports.forEach((entry) => {
      const damages = entry?.findings?.damages;
      if (Array.isArray(damages)) {
        collected.push(...damages);
      }
    });
  }
  return collected;
};

const buildMedia = (workflow, damages) => {
  const media = workflow.media || {};
  const extracted = ensureArray(media.extracted).length > 0
    ? media.extracted
    : ensureArray(workflow.images);
  const frames = ensureArray(media.frames).length > 0
    ? media.frames
    : ensureArray(workflow.processed_images);

  let outlined = ensureArray(media.outlined);
  if (outlined.length === 0) {
    const fromDamages = damages
      .filter((damage) => damage?.outlined_image_url)
      .map((damage) => ({
        damage_id: damage.damage_id || damage.detection_id,
        part: damage.part || damage.part_id,
        type: damage.type || damage.type_code,
        confidence: damage.confidence,
        url: damage.outlined_image_url,
      }));
    outlined = fromDamages;
  }

  return {
    extracted,
    frames,
    outlined,
  };
};

const extractRecommendations = (workflow, report) => {
  if (Array.isArray(workflow.recommendations) && workflow.recommendations.length > 0) {
    return workflow.recommendations;
  }
  const recs = workflow.llm_synthesis?.recommendations;
  if (Array.isArray(recs) && recs.length > 0) {
    return recs;
  }
  const collected = [];
  const reports = report?.reports;
  if (Array.isArray(reports)) {
    reports.forEach((entry) => {
      const recommendations = entry?.analysis?.recommendations;
      if (Array.isArray(recommendations)) {
        collected.push(...recommendations);
      }
    });
  }
  return collected;
};

const extractSummary = (workflow, report, damages) => {
  if (workflow.summary && typeof workflow.summary === 'object') {
    return workflow.summary;
  }
  const summary = report?.summary;
  if (summary && typeof summary === 'object') {
    return summary;
  }
  const breakdown = damages.reduce((acc, damage) => {
    const key = (damage?.type || damage?.type_code || 'inconnu').toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return {
    total_damages: damages.length,
    damages_by_type: breakdown,
  };
};

const extractTotalCost = (workflow, report) =>
  workflow.total_cost || report?.aggregated_totals || report?.totals || report?.grand_total || null;

const extractQA = (workflow, report) =>
  workflow.llm_synthesis?.qa || report?.qa || null;

const extractMissingViews = (report) => {
  if (!report) return [];
  if (Array.isArray(report.missing_views) && report.missing_views.length > 0) {
    return report.missing_views;
  }
  const collected = [];
  const reports = report.reports;
  if (Array.isArray(reports)) {
    reports.forEach((entry) => {
      const requests = entry?.requests_for_additional_media;
      if (Array.isArray(requests)) {
        collected.push(...requests);
      }
    });
  }
  return collected;
};

export const normalizeAnalysisWorkflow = (inspection) => {
  const workflow =
    inspection?.analysis_workflow ||
    inspection?.report_v02?.analysis_workflow ||
    inspection?.yolo_workflow ||
    {};

  const report = workflow.report || inspection?.report_v02 || null;
  const pipeline = inferPipeline(inspection, workflow);
  const version =
    workflow.version ||
    report?.version ||
    inspection?.report_v02?.version ||
    inspection?.version ||
    'v0.2';

  const damages = extractDamages(workflow, report);
  const media = buildMedia(workflow, damages);
  const steps = Array.isArray(workflow.steps) ? workflow.steps : defaultSteps(pipeline);
  const recommendations = extractRecommendations(workflow, report);
  const summary = extractSummary(workflow, report, damages);
  const totalCost = extractTotalCost(workflow, report);
  const qa = extractQA(workflow, report);
  const missingViews = extractMissingViews(report);

  return {
    pipeline,
    version,
    steps,
    report,
    damages,
    media,
    summary,
    totalCost,
    recommendations,
    qa,
    missingViews,
    aiEngine: workflow.ai_engine || null,
    raw: workflow,
  };
};

export const getPipelineLabel = (pipeline) => PIPELINE_LABELS[pipeline] || 'Workflow inconnu';

export const getWorkflowBadge = (workflowModel) => {
  const name = getPipelineLabel(workflowModel.pipeline);
  return `${name} (${workflowModel.version || 'v0.2'})`;
};

